import cookieParser, { CookieSerializeOptions, serialize } from 'cookie';
import dayjs from 'dayjs';
import type { IncomingMessage, ServerResponse } from 'http';

import { COOKIE_DOMAIN } from '@r1-frontend/shared/constants';

type IncomingCookie = { name: string, value: string | number, config?: CookieSerializeOptions };

class Cookies {
    private defaultConfig: CookieSerializeOptions = {
        domain: COOKIE_DOMAIN,
        path: '/',
    };

    constructor(
        private readonly ctx?: { req?: IncomingMessage, res?: ServerResponse },
    ) {
        if (typeof COOKIE_DOMAIN === 'undefined') {
            throw new Error('No value specified for COOKIE_DOMAIN environment variable');
        }
    }

    public set(incomingCookie: IncomingCookie | IncomingCookie[]) {
        if (Array.isArray(incomingCookie)) {
            incomingCookie.forEach(cookie => this.set(cookie));
            return;
        }

        const { name, value } = incomingCookie;
        const config = this.getCookieConfig(incomingCookie.config);

        const serializedCookie = serialize(name, String(value), config); // `${name}=${value}; ${this.serializeCookieConfig(config)}`;
        if (this.ctx && this.ctx.res && this.ctx.req) {
            this.ctx.res.appendHeader('set-cookie', [serializedCookie]);
            this.fillRequestCookies(name, value);
        } else {
            document.cookie = serializedCookie;
        }
    }

    public get(name: string): string | undefined {
        return this.getAll()[name];
    }

    public getAll() {
        const cookies = (this.ctx && this.ctx.res && this.ctx.req)
            ? this.ctx.req.headers.cookie
            : document.cookie;
        return cookieParser.parse(cookies || '');
    }

    public delete(incomingName: string | string[]) {
        if (Array.isArray(incomingName)) {
            incomingName.forEach(name => this.delete(name));
            return;
        }

        const expiredConfig: CookieSerializeOptions = {
            expires: dayjs().subtract(1, 'day').toDate(),
            maxAge: undefined,
            domain: COOKIE_DOMAIN,
        };

        this.set({ name: incomingName, value: '', config: expiredConfig });
    }

    private getCookieConfig(config: CookieSerializeOptions = {}) {
        // todo- пустые поля (config?.maxAge) - тесты
        return { ...this.defaultConfig, ...config };
    }

    // We need to duplicate the cookies in ctx.req
    // to have access to them in the same request thread
    private fillRequestCookies(name: string, value: string | number) {
        if (!this.ctx?.req) {
            return;
        }

        const fromReq = cookieParser.parse(this.ctx.req.headers.cookie ?? '');
        // todo: Не забыть в юнит тесте проверить изначально непустые cookie
        if (!fromReq[name]) {
            if (name in fromReq) {
                delete fromReq[name];
            }
            fromReq[name] = `${value}`;
            this.ctx.req.headers.cookie = Object.entries(fromReq).map(([k, v]) => `${k}=${v}`).join('; ');
        }
    }
}

export default Cookies;
